import { useCanGoBack, useRouter, useRouterState } from '@tanstack/react-router'
import { SCREEN_LG, useMediaQuery, type HeroBack } from '@volee/ui'
import { createContext, useContext, useLayoutEffect } from 'react'

import type { NavigationEntry } from './entries.ts'
import { NAV_PATHS } from './paths.ts'
import { useIsFlow } from './use-is-flow.ts'
import { useNavigationEntries } from './use-navigation-entries.ts'

// The header's arrow below `lg`: spec §4.7, R50. Where it shows, the page's own way back steps aside.

/** A place the menu offers, or home: somewhere you go, not somewhere you are inside. */
export function isMenuDestination(entries: readonly NavigationEntry[], path: string): boolean {
  return path === NAV_PATHS.home || entries.some((entry) => entry.path === path)
}

export interface HeaderBack {
  readonly shown: boolean
  readonly goBack: () => void
}

/** Whether the arrow shows, and where it leads: to the page before, inside this app. A
 *  direct landing has none, and no arrow beats one that guesses. A flow draws none either: its
 *  page keeps its own way back. */
export function useHeaderBack(): HeaderBack {
  const desktop = useMediaQuery(SCREEN_LG)
  const canGoBack = useCanGoBack()
  const router = useRouter()
  const entries = useNavigationEntries()
  const path = useRouterState({ select: (status) => status.location.pathname })
  const flow = useIsFlow()
  return {
    shown: !desktop && !flow && canGoBack && !isMenuDestination(entries, path),
    goBack: () => {
      router.history.back()
    },
  }
}

/** What a page hands the shell to say what the arrow does for now: `null` hands it back. */
export type SetArrowStep = (step: (() => void) | null) => void

export const ArrowStepContext = createContext<SetArrowStep>(() => undefined)

/** While the page stands, the header's arrow does `step` — a callback that keeps its identity —
 *  and not the walk to the page before: for a mode of the page, as the panel that rejects a
 *  request, whose way back closes it. Where the arrow is not drawn the page's own button is. */
export function useArrowStep(step: () => void): void {
  const setStep = useContext(ArrowStepContext)
  useLayoutEffect(() => {
    setStep(step)
    return () => setStep(null)
  }, [setStep, step])
}

/** The page's own way back, unless the header's arrow stands in for it: never two. */
export function useHeroBack(back: HeroBack | undefined): HeroBack | undefined {
  return useHeaderBack().shown ? undefined : back
}
